// CommandPalette.jsx — the Ctrl/⌘+K jump-to-anything dialog.
// WL (Fable Hail Mary Wave 5 — delight item, grounded in QA: gordon-21 holds 408
// wheels across 41 pages of pagination; finding one means paging. This makes it
// instant.)
//
// ADDITIVE: introduces a new overlay; changes no existing navigation. Composes
// Panel + tokens + the shared field chrome (EditorField exports) + WheelThumb.
//
// A11Y: a real modal dialog (role=dialog, aria-modal) using the combobox +
// listbox + aria-activedescendant pattern — focus never leaves the search input,
// arrow keys move a virtual cursor, Enter opens, Esc closes, focus is trapped and
// restored by the parent. ≥44px rows, visible focus, announced result counts,
// honours prefers-reduced-motion.
//
// STATES: empty(recents + hint) · first-run(actions only) · typing/loading ·
// no-results(kind, not cold) · error(retry) · long names(truncate + title) ·
// many results(virtualized via react-window).

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { VariableSizeList } from "react-window";
import { useNavigate } from "react-router-dom";

import { colors } from "../../utils";
import { fontFamily, field, radius, shadow, spacing, zIndex, brand, focusRing } from "../../theme/tokens";
import { fieldChrome } from "../SegmentBuilder/BlockEditorPane/EditorField";
import { getLoggedInUser } from "../../utils/helpers";
import { useWheelSegments } from "../../contexts/wheelSegments";

import { IconSearch, IconEnter, IconArrowUpDown } from "./icons";
import CommandPaletteRow, { CMD_ROW_HEIGHT } from "./CommandPaletteRow";
import { assembleResults, optionDomId } from "./results";
import { buildActions, buildSegments, useWheelSearch } from "./data";
import { getRecents, recordUse } from "./recents";

const HEADER_HEIGHT = 34;
const MAX_LIST_PX = 396; // ~7.5 rows before it scrolls
const DEBOUNCE_MS = 180;

// ── styling ──────────────────────────────────────────────────────────────────
const overlayIn = keyframes`from { opacity: 0 } to { opacity: 1 }`;
const panelIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal + 20};
  background: rgba(18, 48, 81, 0.32);
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 12vh 16px 16px;
  animation: ${overlayIn} 140ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 560px;
  background: ${colors.white};
  border-radius: ${radius.lg}px;
  box-shadow: ${shadow.e3};
  border: 1px solid ${colors.lightBorder};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${panelIn} 160ms cubic-bezier(0.2, 0, 0, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SearchBar = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  column-gap: ${spacing.md}px;
  padding: 0 ${spacing.lg}px;
  height: 56px;
  border-bottom: 1px solid ${colors.lightBorder};
  color: ${field.muted};
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: ${fontFamily};
  font-size: 16px;
  color: ${field.ink};
  width: 100%;
  height: 100%;
  &::placeholder {
    color: ${fieldChrome.placeholder};
  }
`;

const Kbd = styled.kbd`
  font-family: ${fontFamily};
  font-size: 11px;
  font-weight: 600;
  color: ${field.muted};
  background: #f2f4f7;
  border: 1px solid ${colors.lightBorder};
  border-radius: ${radius.sm}px;
  padding: 2px 6px;
  line-height: 1.4;
`;

const ListShell = styled.div`
  position: relative;
  padding: ${spacing.sm}px;
`;

const indeterminate = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
`;

const Progress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  overflow: hidden;
  background: transparent;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    width: 40%;
    background: ${brand.primary};
    animation: ${indeterminate} 1s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    &::after { animation: none; width: 100%; opacity: 0.5; }
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  height: ${HEADER_HEIGHT}px;
  padding: 0 ${spacing.md}px;
  font-family: ${fontFamily};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${field.muted};
`;

const StatusBlock = styled.div`
  padding: ${spacing.xxl}px ${spacing.lg}px;
  text-align: center;
  font-family: ${fontFamily};
  color: ${field.muted};
`;

const StatusTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${field.ink};
  margin-bottom: 4px;
`;

const StatusHint = styled.div`
  font-size: 12.5px;
  color: ${field.muted};
`;

const RetryButton = styled.button`
  margin-top: ${spacing.md}px;
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 600;
  color: ${brand.primary};
  background: #f1e9fe;
  border: 1px solid #e4d5fd;
  border-radius: ${radius.sm}px;
  min-height: 40px;
  padding: 0 ${spacing.lg}px;
  cursor: pointer;
  &:hover { background: #e9dcfd; }
  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing.shadow};
  }
`;

const shimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
`;

const Skeleton = styled.div`
  height: ${CMD_ROW_HEIGHT}px;
  margin: 4px ${spacing.md}px;
  border-radius: ${radius.md}px;
  background: linear-gradient(90deg, #f2f4f7 25%, #e9edf2 37%, #f2f4f7 63%);
  background-size: 400% 100%;
  animation: ${shimmer} 1.4s ease infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.md}px;
  padding: ${spacing.sm}px ${spacing.lg}px;
  border-top: 1px solid ${colors.lightBorder};
  font-family: ${fontFamily};
  font-size: 11.5px;
  color: ${field.muted};
`;

const FooterHints = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg}px;
`;

const Hint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const srOnly = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// Module-level virtualized row so its component identity is stable across
// renders (react-window reconciles by type — an inline component would remount
// every row on each keystroke). All varying data arrives via `itemData`.
const VirtualRow = ({ index, style, data }) => {
  const { flatRows, active, onSelect, onHoverOption } = data;
  const row = flatRows[index];
  if (row.type === "header") {
    return (
      <SectionHeader style={style} role="presentation">
        {row.title}
      </SectionHeader>
    );
  }
  const isActive = row.optionIndex === active;
  return (
    <CommandPaletteRow
      style={style}
      item={row.item}
      indices={row.indices}
      active={isActive}
      id={optionDomId(row.item.id)}
      onSelect={onSelect}
      onHover={() => !isActive && onHoverOption(row.optionIndex)}
    />
  );
};

// ── component ────────────────────────────────────────────────────────────────
const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { segments = [], wheelData } = useWheelSegments() || {};
  const userId = getLoggedInUser()?.id;

  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState([]);

  const inputRef = useRef(null);
  const dialogRef = useRef(null);
  const listRef = useRef(null);
  const activeRef = useRef(0);

  // Load recents + reset transient state each time the palette opens.
  useEffect(() => {
    if (!open) return;
    setRecents(getRecents());
    setQuery("");
    setDebounced("");
    setActive(0);
  }, [open]);

  // Debounce ONLY the server wheel search; action/segment filtering is instant.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const actions = useMemo(() => buildActions(userId), [userId]);
  const segmentItems = useMemo(
    () => buildSegments(segments, wheelData),
    [segments, wheelData]
  );

  const wheelSearch = useWheelSearch(debounced, userId, open);
  const wheelItems = useMemo(() => wheelSearch.data?.items || [], [wheelSearch.data]);

  const { flatRows, options } = useMemo(
    () =>
      assembleResults({
        query,
        actions,
        segments: segmentItems,
        wheelItems,
        recents,
      }),
    [query, actions, segmentItems, wheelItems, recents]
  );

  // Clamp active into range whenever the option set changes.
  useEffect(() => {
    setActive((a) => (options.length === 0 ? 0 : Math.min(a, options.length - 1)));
  }, [options.length]);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // Reset the virtualized size cache and scroll the active row into view.
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.resetAfterIndex(0);
  }, [flatRows]);

  useEffect(() => {
    if (listRef.current && options[active]) {
      listRef.current.scrollToItem(options[active].flatIndex, "smart");
    }
  }, [active, options]);

  const q = query.trim();
  const searching = wheelSearch.isSearching;
  const isError = q !== "" && wheelSearch.isError;
  const noResults = q !== "" && options.length === 0 && !searching && !isError;

  const select = useCallback(
    (item) => {
      if (!item) return;
      recordUse({
        id: item.id,
        kind: item.kind,
        label: item.label,
        sublabel: item.sublabel,
        path: item.path,
        portrait: item.portrait,
        wheelName: item.wheelName,
      });
      onClose();
      // Defer navigation until after close so focus restore + unmount settle.
      requestAnimationFrame(() => navigate(item.path));
    },
    [navigate, onClose]
  );

  const move = useCallback(
    (delta) => {
      setActive((a) => {
        if (options.length === 0) return 0;
        const next = (a + delta + options.length) % options.length;
        return next;
      });
    },
    [options.length]
  );

  const onInputKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          move(1);
          break;
        case "ArrowUp":
          e.preventDefault();
          move(-1);
          break;
        case "Home":
          e.preventDefault();
          setActive(0);
          break;
        case "End":
          e.preventDefault();
          setActive(Math.max(0, options.length - 1));
          break;
        case "PageDown":
          e.preventDefault();
          move(5);
          break;
        case "PageUp":
          e.preventDefault();
          move(-5);
          break;
        case "Enter":
          e.preventDefault();
          if (options[activeRef.current]) select(options[activeRef.current].item);
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    },
    [move, options, select, onClose]
  );

  // Focus trap: keep Tab within the dialog (input ↔ any button).
  const onDialogKeyDown = useCallback((e) => {
    if (e.key !== "Tab") return;
    const focusables = dialogRef.current?.querySelectorAll(
      'input, button, [href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables || focusables.length === 0) return;
    const list = Array.from(focusables).filter((el) => !el.disabled);
    const first = list[0];
    const last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  // Focus the input on open.
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  if (!open) return null;

  const activeItem = options[active]?.item;
  const activeId = activeItem ? optionDomId(activeItem.id) : undefined;

  // Announced result summary (aria-live).
  const countMsg =
    q === ""
      ? `${options.length} suggestions. Type to search.`
      : searching
      ? "Searching…"
      : `${options.length} result${options.length === 1 ? "" : "s"}`;

  // Virtualized list height auto-fits content, capped so it scrolls when long.
  const contentHeight = flatRows.reduce(
    (sum, r) => sum + (r.type === "header" ? HEADER_HEIGHT : CMD_ROW_HEIGHT),
    0
  );
  const listHeight = Math.min(contentHeight, MAX_LIST_PX);

  const itemSize = (i) => (flatRows[i].type === "header" ? HEADER_HEIGHT : CMD_ROW_HEIGHT);

  return createPortal(
    <Overlay
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onDialogKeyDown}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <SearchBar>
          <IconSearch />
          <SearchInput
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onInputKeyDown}
            placeholder="Search wheels, segments and actions…"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-listbox"
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            aria-label="Search wheels, segments and actions"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />
          <Kbd aria-hidden="true">esc</Kbd>
        </SearchBar>

        {/* Live region: announce the result count to screen readers. */}
        <span aria-live="polite" style={srOnly}>
          {countMsg}
        </span>

        <ListShell>
          {searching && options.length > 0 && <Progress aria-hidden="true" />}

          {isError ? (
            <StatusBlock role="alert">
              <StatusTitle>Couldn’t reach your wheels</StatusTitle>
              <StatusHint>Check your connection and try again.</StatusHint>
              <RetryButton type="button" onClick={() => wheelSearch.refetch()}>
                Retry
              </RetryButton>
            </StatusBlock>
          ) : noResults ? (
            <StatusBlock>
              <StatusTitle>No matches for “{q}”</StatusTitle>
              <StatusHint>Try fewer letters, or a different word.</StatusHint>
            </StatusBlock>
          ) : searching && options.length === 0 ? (
            <div aria-hidden="true">
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </div>
          ) : (
            <>
              {q === "" && recents.length === 0 && (
                <StatusHint style={{ padding: `${spacing.sm}px ${spacing.md}px` }}>
                  Jump to any of your wheels, segments or actions — start typing.
                </StatusHint>
              )}
              <div id="cmdk-listbox" role="listbox" aria-label="Results">
              <VariableSizeList
                ref={listRef}
                height={listHeight}
                width="100%"
                itemCount={flatRows.length}
                itemSize={itemSize}
                overscanCount={6}
                itemData={{
                  flatRows,
                  active,
                  onSelect: select,
                  onHoverOption: setActive,
                }}
                style={{ overflowX: "hidden" }}
              >
                {VirtualRow}
              </VariableSizeList>
              </div>
            </>
          )}
        </ListShell>

        <Footer>
          <FooterHints>
            <Hint>
              <IconArrowUpDown size={14} /> Navigate
            </Hint>
            <Hint>
              <IconEnter size={14} /> Open
            </Hint>
            <Hint>
              <Kbd aria-hidden="true">esc</Kbd> Close
            </Hint>
          </FooterHints>
          <span>
            {options.length} {options.length === 1 ? "result" : "results"}
          </span>
        </Footer>
      </Dialog>
    </Overlay>,
    document.body
  );
};

export default CommandPalette;
